import { useMemo } from "react";
import { MapPin, Zap, Globe, Network, Building2 } from "lucide-react";
import { SectionCard, Segmented, InfoNote, Row } from "@/components/common/Cards";
import { Field, MoneyInput, NumInput } from "@/components/common/Inputs";
import { DEFAULT_LOCATION } from "@/lib/engines/hosting";

export default function LocationPanel({ servers, locations, setLocations, readOnly }) {
  const unique = useMemo(() => {
    const map = {};
    (servers || []).forEach((s) => (map[s.location] = (map[s.location] || 0) + 1));
    return Object.entries(map).sort((a, b) => b[1] - a[1]);
  }, [servers]);

  const update = (name, patch) =>
    setLocations((lx) => ({ ...lx, [name]: { ...DEFAULT_LOCATION, ...(lx[name] || {}), ...patch } }));

  return (
    <div className="space-y-5">
      <InfoNote>
        Masukkan tarif per lokasi: biaya colocation (per-U atau per-rack), listrik, bandwidth upstream, dan harga IPv4. Nilai dibagi proporsional ke tiap server di lokasi tersebut.
      </InfoNote>
      <div className="grid lg:grid-cols-2 gap-5">
        {unique.map(([name, count]) => {
          const l = { ...DEFAULT_LOCATION, ...(locations[name] || {}) };
          return (
            <SectionCard
              key={name}
              title={name}
              eyebrow={`${count} server`}
              testid={`hosting-location-${name}`}
            >
              <div className="space-y-4">
                <Field label="Mode biaya colocation">
                  <Segmented
                    value={l.colocationMode}
                    onChange={(v) => update(name, { colocationMode: v })}
                    size="sm"
                    testid={`hosting-location-${name}-mode`}
                    options={[
                      { value: "per_u", label: "Per U rack-unit" },
                      { value: "per_rack", label: "Per rack penuh" },
                    ]}
                  />
                </Field>
                {l.colocationMode === "per_u" ? (
                  <Field label="Biaya colocation per U / bulan">
                    <MoneyInput value={l.colocationPerU} onChange={(v) => update(name, { colocationPerU: v })} disabled={readOnly} testid={`hosting-location-${name}-peru`} />
                  </Field>
                ) : (
                  <div className="grid grid-cols-2 gap-3">
                    <Field label="Biaya rack/bulan">
                      <MoneyInput value={l.rackMonthly} onChange={(v) => update(name, { rackMonthly: v })} disabled={readOnly} testid={`hosting-location-${name}-rack`} />
                    </Field>
                    <Field label="Server per rack">
                      <NumInput value={l.serversPerRack} onChange={(v) => update(name, { serversPerRack: v })} decimals={0} disabled={readOnly} testid={`hosting-location-${name}-perrack`} />
                    </Field>
                  </div>
                )}
                <Field label="Mode biaya listrik">
                  <Segmented
                    value={l.powerMode}
                    onChange={(v) => update(name, { powerMode: v })}
                    size="sm"
                    testid={`hosting-location-${name}-powermode`}
                    options={[
                      { value: "included", label: "Termasuk sewa" },
                      { value: "metered", label: "Hitung dari kWh" },
                    ]}
                  />
                </Field>
                {l.powerMode === "metered" && (
                  <div className="grid grid-cols-2 gap-3">
                    <Field label="Tarif listrik">
                      <MoneyInput value={l.tariffKwh} onChange={(v) => update(name, { tariffKwh: v })} disabled={readOnly} testid={`hosting-location-${name}-tariff`} />
                    </Field>
                    <Field label="PUE (overhead)">
                      <NumInput value={l.pue} onChange={(v) => update(name, { pue: v })} decimals={2} disabled={readOnly} testid={`hosting-location-${name}-pue`} />
                    </Field>
                  </div>
                )}
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Bandwidth kontrak (Mbps)">
                    <NumInput value={l.bandwidthMbps} onChange={(v) => update(name, { bandwidthMbps: v })} suffix="Mbps" decimals={0} disabled={readOnly} testid={`hosting-location-${name}-bw`} />
                  </Field>
                  <Field label="Harga per Mbps/bulan">
                    <MoneyInput value={l.bandwidthPricePerMbps} onChange={(v) => update(name, { bandwidthPricePerMbps: v })} disabled={readOnly} testid={`hosting-location-${name}-bwprice`} />
                  </Field>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Harga IPv4 / bulan">
                    <MoneyInput value={l.ipPricePerMonth} onChange={(v) => update(name, { ipPricePerMonth: v })} disabled={readOnly} testid={`hosting-location-${name}-ip`} />
                  </Field>
                  <Field label="Biaya lain / bulan">
                    <MoneyInput value={l.otherMonthly} onChange={(v) => update(name, { otherMonthly: v })} disabled={readOnly} testid={`hosting-location-${name}-other`} />
                  </Field>
                </div>
              </div>
            </SectionCard>
          );
        })}
        {unique.length === 0 && (
          <SectionCard title="Tidak ada lokasi">
            <p className="text-sm text-muted-foreground">Belum ada server di-seed. Tambah lewat Admin → Inventory terlebih dahulu.</p>
          </SectionCard>
        )}
      </div>
    </div>
  );
}
